import React, { useState } from 'react';
import { Trash2, Package, Clock, Lock, AlertTriangle, Check, Download, Info, Loader2 } from 'lucide-react';
import api from '../services/api';

type TabType = 'delete' | 'export' | 'retention';

export default function GDPRAdmin() {
  const [activeTab, setActiveTab] = useState<TabType>('delete');
  
  // Delete form state
  const [deleteForm, setDeleteForm] = useState({
    email: '',
    requested_by: '',
    ticket: '',
    legal_approval: false,
    retain_audit_logs: true
  });
  const [deleteResult, setDeleteResult] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Export form state
  const [exportEmail, setExportEmail] = useState('');
  const [exportResult, setExportResult] = useState<any>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  
  // Retention state
  const [retentionResult, setRetentionResult] = useState<any>(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionError, setRetentionError] = useState<string | null>(null);

  // Button hover states
  const [deleteHovered, setDeleteHovered] = useState(false);
  const [exportHovered, setExportHovered] = useState(false);
  const [retentionHovered, setRetentionHovered] = useState(false);
  const [downloadHovered, setDownloadHovered] = useState(false);

  async function handleDelete(e: React.FormEvent) {
    e.preventDefault();
    
    if (!deleteForm.legal_approval) {
      alert('Legal approval is required for GDPR deletion!');
      return;
    }
    
    if (!deleteForm.retain_audit_logs) {
      if (!window.confirm('WARNING: Anonymizing audit logs will impact troubleshooting. Are you sure legal requires this?')) {
        return;
      }
    }
    
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      
      const response = await api.post('/gdpr/right-to-be-forgotten', deleteForm);
      setDeleteResult(response.data);
      
      setDeleteForm({
        email: '',
        requested_by: '',
        ticket: '',
        legal_approval: false,
        retain_audit_logs: true
      });
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to process GDPR deletion');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleExport(e: React.FormEvent) {
    e.preventDefault();
    
    try {
      setExportLoading(true);
      setExportError(null);
      
      const response = await api.post('/gdpr/export-data', { email: exportEmail });
      setExportResult(response.data);
    } catch (err: any) {
      setExportError(err.message || 'Failed to export data');
    } finally {
      setExportLoading(false);
    }
  }

  async function handleEnforceRetention() {
    if (!window.confirm('This will anonymize audit logs older than 7 years. Continue?')) {
      return;
    }
    
    try {
      setRetentionLoading(true);
      setRetentionError(null);
      
      const response = await api.post('/gdpr/enforce-retention', {});
      setRetentionResult(response.data);
    } catch (err: any) {
      setRetentionError(err.message || 'Failed to enforce retention');
    } finally {
      setRetentionLoading(false);
    }
  }

  const handleDownload = () => {
    if (!exportResult) return;
    const blob = new Blob([JSON.stringify(exportResult.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gdpr-export-${exportEmail}-${Date.now()}.json`;
    a.click();
  };

  const tabs = [
    { id: 'delete' as TabType, label: 'Right to be Forgotten', icon: Trash2 },
    { id: 'export' as TabType, label: 'Data Export', icon: Package },
    { id: 'retention' as TabType, label: 'Retention Policy', icon: Clock },
  ];

  // Styles
  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-sm)',
    padding: '24px',
    fontFamily: 'var(--font-sans)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 700,
    color: 'var(--v2-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    color: isActive ? 'var(--v2-primary)' : 'var(--v2-text-tertiary)',
    fontSize: '0.875rem',
    fontWeight: isActive ? 600 : 500,
    cursor: 'pointer',
    borderBottom: isActive ? '2px solid var(--v2-primary)' : '2px solid transparent',
    marginBottom: '-1px',
    transition: 'all 0.15s',
  });

  const alertStyle = (type: 'warning' | 'error' | 'success' | 'info'): React.CSSProperties => {
    const colors = {
      warning: { bg: 'var(--v2-warning-bg)', border: '#d97706', text: '#92400e' },
      error: { bg: 'var(--v2-error-bg)', border: 'var(--v2-error)', text: 'var(--v2-error)' },
      success: { bg: 'var(--v2-success-bg)', border: 'var(--v2-success)', text: 'var(--v2-success-dark)' },
      info: { bg: 'var(--v2-info-bg)', border: 'var(--v2-primary)', text: 'var(--v2-primary)' },
    };
    const c = colors[type];
    return {
      padding: '16px',
      backgroundColor: c.bg,
      borderLeft: `4px solid ${c.border}`,
      borderRadius: 'var(--radius-md)',
      color: c.text,
      fontSize: '0.875rem',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
    };
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '16px',
  };

  const checkboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    accentColor: 'var(--v2-primary)',
    marginTop: '2px',
  };

  const buttonStyle = (color: 'primary' | 'danger' | 'secondary', hovered: boolean, disabled: boolean): React.CSSProperties => {
    const colors = {
      primary: { bg: 'var(--v2-primary)', hoverBg: 'var(--v2-primary-hover)', text: '#ffffff' },
      danger: { bg: 'var(--v2-error)', hoverBg: '#b91c1c', text: '#ffffff' },
      secondary: { bg: 'var(--v2-surface)', hoverBg: 'var(--v2-surface-hover)', text: 'var(--v2-text)' },
    };
    const c = colors[color];
    return {
      width: '100%',
      padding: '12px 20px',
      backgroundColor: disabled ? 'var(--v2-border)' : hovered ? c.hoverBg : c.bg,
      color: disabled ? 'var(--v2-text-muted)' : c.text,
      border: color === 'secondary' ? '1px solid var(--v2-border)' : 'none',
      borderRadius: 'var(--radius-md)',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    };
  };

  const preStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface-alt)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    padding: '16px',
    fontSize: '0.75rem',
    fontFamily: 'var(--font-mono)',
    overflow: 'auto',
    maxHeight: '300px',
    color: 'var(--v2-text)',
  };

  return (
    <div style={{ fontFamily: 'var(--font-sans)' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--v2-primary-light)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Lock size={20} strokeWidth={1.5} color="var(--v2-primary)" />
        </div>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
            GDPR Compliance Admin
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
            Super Admin only — Process verified GDPR requests
          </p>
        </div>
      </div>

      {/* Warning */}
      <div style={alertStyle('warning')}>
        <AlertTriangle size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
        <span>
          <strong>Important:</strong> These are NOT for normal user deletions. 
          Only use for verified GDPR "Right to be Forgotten" requests with legal approval.
        </span>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '4px', 
        borderBottom: '1px solid var(--v2-border)', 
        marginBottom: '24px' 
      }}>
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={tabStyle(activeTab === tab.id)}
              onMouseOver={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = 'var(--v2-text)';
                  e.currentTarget.style.backgroundColor = 'var(--v2-surface-alt)';
                }
              }}
              onMouseOut={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = 'var(--v2-text-tertiary)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <TabIcon size={16} strokeWidth={1.5} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Right to be Forgotten Tab */}
      {activeTab === 'delete' && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 20px 0' }}>
            Process GDPR Deletion Request
          </h3>

          <form onSubmit={handleDelete}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>User Email *</label>
              <input
                type="email"
                required
                value={deleteForm.email}
                onChange={(e) => setDeleteForm({ ...deleteForm, email: e.target.value })}
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--v2-primary)';
                  e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--v2-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Requested By *</label>
              <input
                type="text"
                required
                placeholder="support@company.com"
                value={deleteForm.requested_by}
                onChange={(e) => setDeleteForm({ ...deleteForm, requested_by: e.target.value })}
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--v2-primary)';
                  e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--v2-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Support Ticket ID *</label>
              <input
                type="text"
                required
                placeholder="GDPR-2025-001"
                value={deleteForm.ticket}
                onChange={(e) => setDeleteForm({ ...deleteForm, ticket: e.target.value })}
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--v2-primary)';
                  e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--v2-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                id="retain_audit"
                checked={deleteForm.retain_audit_logs}
                onChange={(e) => setDeleteForm({ ...deleteForm, retain_audit_logs: e.target.checked })}
                style={checkboxStyle}
              />
              <label htmlFor="retain_audit" style={{ fontSize: '0.875rem', color: 'var(--v2-text)', cursor: 'pointer' }}>
                Retain audit logs (recommended — allows troubleshooting)
              </label>
            </div>

            <div style={checkboxContainerStyle}>
              <input
                type="checkbox"
                id="legal_approval"
                checked={deleteForm.legal_approval}
                onChange={(e) => setDeleteForm({ ...deleteForm, legal_approval: e.target.checked })}
                style={checkboxStyle}
              />
              <label htmlFor="legal_approval" style={{ fontSize: '0.875rem', color: 'var(--v2-text)', cursor: 'pointer' }}>
                <strong>Legal team has reviewed and approved this deletion *</strong>
              </label>
            </div>

            {deleteError && (
              <div style={alertStyle('error')}>
                <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                <span>{deleteError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={deleteLoading || !deleteForm.legal_approval}
              onMouseOver={() => setDeleteHovered(true)}
              onMouseOut={() => setDeleteHovered(false)}
              style={buttonStyle('danger', deleteHovered, deleteLoading || !deleteForm.legal_approval)}
            >
              {deleteLoading ? (
                <>
                  <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  Processing...
                </>
              ) : (
                <>
                  <Trash2 size={16} strokeWidth={1.5} />
                  Process GDPR Deletion
                </>
              )}
            </button>
          </form>

          {deleteResult && (
            <div style={{ ...alertStyle('success'), marginTop: '20px', marginBottom: 0, flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Check size={18} strokeWidth={2} />
                <strong>GDPR Deletion Completed</strong>
              </div>
              <pre style={preStyle}>
                {JSON.stringify(deleteResult.report, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Data Export Tab */}
      {activeTab === 'export' && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 8px 0' }}>
            Export User Data
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '0 0 20px 0' }}>
            GDPR Data Portability — Export all data associated with a user
          </p>

          <form onSubmit={handleExport}>
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>User Email *</label>
              <input
                type="email"
                required
                value={exportEmail}
                onChange={(e) => setExportEmail(e.target.value)}
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--v2-primary)';
                  e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--v2-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {exportError && (
              <div style={alertStyle('error')}>
                <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                <span>{exportError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={exportLoading}
              onMouseOver={() => setExportHovered(true)}
              onMouseOut={() => setExportHovered(false)}
              style={buttonStyle('primary', exportHovered, exportLoading)}
            >
              {exportLoading ? (
                <>
                  <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  Exporting...
                </>
              ) : (
                <>
                  <Package size={16} strokeWidth={1.5} />
                  Export Data
                </>
              )}
            </button>
          </form>

          {exportResult && (
            <div style={{ ...alertStyle('info'), marginTop: '20px', marginBottom: 0, flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Package size={18} strokeWidth={1.5} />
                <strong>Data Export Complete</strong>
              </div>
              <pre style={preStyle}>
                {JSON.stringify(exportResult.data, null, 2)}
              </pre>
              <button
                type="button"
                onClick={handleDownload}
                onMouseOver={() => setDownloadHovered(true)}
                onMouseOut={() => setDownloadHovered(false)}
                style={{
                  ...buttonStyle('secondary', downloadHovered, false),
                  width: 'auto',
                  marginTop: '12px',
                }}
              >
                <Download size={16} strokeWidth={1.5} />
                Download JSON
              </button>
            </div>
          )}
        </div>
      )}

      {/* Retention Policy Tab */}
      {activeTab === 'retention' && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 8px 0' }}>
            Enforce Retention Policy
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '0 0 20px 0' }}>
            Anonymize audit logs older than 7 years (2,555 days). This is typically run as a scheduled job.
          </p>

          <div style={alertStyle('info')}>
            <Info size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>
              <strong>Note:</strong> This will anonymize PII in audit logs past the retention period
              while keeping the audit trail structure intact.
            </span>
          </div>

          {retentionError && (
            <div style={alertStyle('error')}>
              <AlertTriangle size={16} strokeWidth={1.5} style={{ flexShrink: 0 }} />
              <span>{retentionError}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleEnforceRetention}
            disabled={retentionLoading}
            onMouseOver={() => setRetentionHovered(true)}
            onMouseOut={() => setRetentionHovered(false)}
            style={buttonStyle('primary', retentionHovered, retentionLoading)}
          >
            {retentionLoading ? (
              <>
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                Processing...
              </>
            ) : (
              <>
                <Clock size={16} strokeWidth={1.5} />
                Enforce Retention Policy
              </>
            )}
          </button>

          {retentionResult && (
            <div style={{ ...alertStyle('success'), marginTop: '20px', marginBottom: 0, flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Check size={18} strokeWidth={2} />
                <strong>Retention Policy Enforced</strong>
              </div>
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #86efac', paddingBottom: '8px' }}>
                  <span style={{ fontWeight: 500 }}>Logs Processed:</span>
                  <span>{retentionResult.result.logs_processed}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #86efac', paddingBottom: '8px' }}>
                  <span style={{ fontWeight: 500 }}>Logs Anonymized:</span>
                  <span>{retentionResult.result.logs_anonymized}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #86efac', paddingBottom: '8px' }}>
                  <span style={{ fontWeight: 500 }}>Cutoff Date:</span>
                  <span>{new Date(retentionResult.result.cutoff_date).toLocaleDateString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 500 }}>Retention Period:</span>
                  <span>{retentionResult.result.retention_days} days</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
